/**
 * CSK demo catalog data — the single source of truth for the demo
 * inventory, plus the small helpers over it.
 *
 * This is a leaf module: it imports only the JSON SSOT
 * (`demos/demo-catalog.json`) and nothing from the `@csk/demo-ui` graph, so
 * `DemoNav` / `DemoLauncher` can depend on it without forming a cycle
 * through the `./index.ts` barrel, and the lightweight
 * `@csk/demo-ui/catalog` subpath stays free of `@csk/core`.
 *
 * Catalog entries come in two shapes, discriminated by `topology`:
 *
 *   - `topology: "workspace"` (default) — a runnable Vite workspace at
 *     `demos/<name>/(ui|app|src)/`, served on its own fixed port via
 *     `npm run demo:<name>`. Carries `package`, `workspace`, `port`,
 *     `script`. This is the shape every catalog entry has today.
 *
 *   - `topology: "hub-internal"` — a demo whose source lives inside the
 *     hub workspace at `demos/hub/src/apps/<slug>/`. Carries `slug` and
 *     `directory`; no `package`, no `port`, no `script`. Served only by
 *     the hub itself (`npm run demo:hub`, then navigate to `/<slug>`).
 *     This is the post-Phase-5 shape; introduced now so that the SSOT
 *     and the drift checker can represent the two topologies side-by-side
 *     while Phase 5 migrations roll out one demo at a time.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import catalogData from "../../../demo-catalog.json" with { type: "json" };

/** Fields every catalog entry carries, regardless of topology. */
interface DemoCatalogEntryCommon {
  /** Short human title. */
  readonly title: string;
  /** One-line "what you'll see" (serve guide + README table). */
  readonly blurb: string;
  /** Slightly longer description (catalog + launcher cards). */
  readonly summary: string;
}

/** A demo that still lives in its own runnable Vite workspace. */
export interface DemoCatalogWorkspaceEntry extends DemoCatalogEntryCommon {
  readonly topology?: "workspace";
  /** Root `package.json` script that serves this demo (e.g. `demo:app`). */
  readonly script: string;
  /** npm workspace package name. */
  readonly package: string;
  /** Workspace directory, relative to the repo root. */
  readonly workspace: string;
  /** Fixed dev-server port, so several demos can run at once. */
  readonly port: number;
}

/** A demo whose source has been folded into the hub workspace. */
export interface DemoCatalogHubInternalEntry extends DemoCatalogEntryCommon {
  readonly topology: "hub-internal";
  /** URL path component the hub serves this demo under (e.g. `seam-bridge`). */
  readonly slug: string;
  /**
   * Source directory under the hub workspace, relative to the repo root
   * (e.g. `demos/hub/src/apps/seam-bridge`). Must contain an `App.tsx`
   * with a named `export function App()` import target.
   */
  readonly directory: string;
}

/** One catalog entry — either topology. */
export type DemoCatalogEntry = DemoCatalogWorkspaceEntry | DemoCatalogHubInternalEntry;

/**
 * Every demo in the catalog, in canonical presentation order (the hub
 * meta-app first). Frozen so consumers cannot mutate the shared catalog.
 */
export const DEMO_CATALOG: readonly DemoCatalogEntry[] = Object.freeze(
  (catalogData.demos as readonly DemoCatalogEntry[]).map((entry) => Object.freeze({ ...entry })),
);

/** True iff this entry still lives in its own runnable Vite workspace. */
export function isWorkspaceEntry(entry: DemoCatalogEntry): entry is DemoCatalogWorkspaceEntry {
  return entry.topology === undefined || entry.topology === "workspace";
}

/** True iff this entry has been folded into the hub workspace. */
export function isHubInternalEntry(entry: DemoCatalogEntry): entry is DemoCatalogHubInternalEntry {
  return entry.topology === "hub-internal";
}

/**
 * The URL slug the hub uses for this demo. For workspace entries, it is
 * derived from the `demo:*` script by stripping the `demo:` prefix; for
 * hub-internal entries, it is the explicit `slug` field.
 */
export function entrySlug(entry: DemoCatalogEntry): string {
  if (isHubInternalEntry(entry)) {
    return entry.slug;
  }
  return entry.script.replace(/^demo:/, "");
}

const workspaceEntries: DemoCatalogWorkspaceEntry[] = [];
const hubInternalEntries: DemoCatalogHubInternalEntry[] = [];
const entriesBySlug = new Map<string, DemoCatalogEntry>();
const workspaceEntriesByScript = new Map<string, DemoCatalogWorkspaceEntry>();

for (const entry of DEMO_CATALOG) {
  entriesBySlug.set(entrySlug(entry), entry);
  if (isWorkspaceEntry(entry)) {
    workspaceEntries.push(entry);
    workspaceEntriesByScript.set(entry.script, entry);
  } else {
    hubInternalEntries.push(entry);
  }
}

/** Workspace demos in catalog presentation order. */
export const WORKSPACE_DEMO_CATALOG: readonly DemoCatalogWorkspaceEntry[] =
  Object.freeze(workspaceEntries);

/** Hub-internal demos in catalog presentation order. */
export const HUB_INTERNAL_DEMO_CATALOG: readonly DemoCatalogHubInternalEntry[] =
  Object.freeze(hubInternalEntries);

/**
 * In-hub path for the demo (`/<slug>`). Both topologies render under the
 * same scheme — the only difference is that a workspace demo also runs on
 * its own per-port dev server.
 */
export function entryHubPath(entry: DemoCatalogEntry): string {
  return `/${entrySlug(entry)}`;
}

/**
 * Per-port dev URL for a workspace demo, or `null` for a hub-internal
 * demo (which has no per-port server — visit the hub instead).
 */
export function entryLocalUrl(entry: DemoCatalogEntry): string | null {
  if (isWorkspaceEntry(entry)) {
    return `http://localhost:${String(entry.port)}/`;
  }
  return null;
}

/**
 * Legacy alias for `entryLocalUrl` that asserts a workspace entry. Kept
 * because callers that already constrain themselves to workspace entries
 * (e.g. the per-port nav bar) want the non-null return type.
 */
export function demoLocalUrl(entry: DemoCatalogWorkspaceEntry): string {
  return `http://localhost:${String(entry.port)}/`;
}

/** Look up a catalog entry by its `demo:*` script. Workspace-only. */
export function demoByScript(script: string): DemoCatalogWorkspaceEntry | undefined {
  return workspaceEntriesByScript.get(script);
}

/** Look up a catalog entry by its slug. Works across both topologies. */
export function demoBySlug(slug: string): DemoCatalogEntry | undefined {
  return entriesBySlug.get(slug);
}
